import React from 'react';
import { FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsPhone } from '../../hooks/UseIsCompact';
import { AppModal } from './AppModal';
import { NumberCell } from './NumberCell';
import { StatusBadge } from './StatusBadge';
import type { BulkExcludeGroup } from './BulkActionBar';

/** 미리보기 한 줄 — 카드 · 지금 값 → 바뀔 값. 값이 없으면 null(미수집). */
export interface BulkPreviewItem {
  key: React.Key;
  name: string;
  from: number | null | undefined;
  to: number | null | undefined;
}

/** 미리보기에 그리는 줄 수 상한(설계 §2-2 — 최대 5줄 + 외 N건). */
export const BULK_PREVIEW_ROWS = 5;

/** 넓은 화면 표의 금액 · 차이 열 폭 — 7자리 금액 두 개(`1,234,567원 → 1,234,567원`) · `▼ −1,234,567 (−99.9%)` 가 한 줄에 들어가는 폭. */
const PRICE_COL_W = 184;
const DIFF_COL_W = 136;

/** 카드 이름 2줄 말줄임(코덱 리뷰 #717 — 긴 이름이 모달 폭을 넘었다). 전체 이름은 title 로. */
const clamp2: React.CSSProperties = {
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-all',
};

const absDiff = (i: BulkPreviewItem) => (i.from != null && i.to != null ? Math.abs(i.to - i.from) : -1);

/** 변동이 큰 순으로 앞 5줄 — 값이 없는 줄은 뒤로. 같은 크기는 받은 순서 그대로. */
export function previewRows(items: BulkPreviewItem[], limit = BULK_PREVIEW_ROWS): BulkPreviewItem[] {
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => absDiff(b.it) - absDiff(a.it) || a.i - b.i)
    .slice(0, limit)
    .map(({ it }) => it);
}

/** 차이 글 — `+1,200 (+9.8%)` · 부호 + 글자(▲/▼) + 색, 색만으로 구분하지 않는다. */
export function diffText(from: number | null | undefined, to: number | null | undefined): string {
  if (from == null || to == null) return '-';
  const d = to - from;
  if (d === 0) return '변동 없음';
  const sign = d > 0 ? '+' : '−';
  const arrow = d > 0 ? '▲' : '▼';
  const pct = from > 0 ? ` (${sign}${(Math.abs(d) / from * 100).toFixed(1)}%)` : '';
  return `${arrow} ${sign}${Math.abs(d).toLocaleString('ko-KR')}${pct}`;
}

export interface BulkPreviewModalProps {
  open: boolean;
  title: React.ReactNode;
  /** 실제로 보낼 줄 전부(앞 5줄만 그리고 나머지는 '외 N건'). */
  items: BulkPreviewItem[];
  /** 열 머리 — 지금 값 · 바뀔 값이 무엇인지(예: '광주 가격' → '요청 값(네이버 가격)'). */
  fromLabel: string;
  toLabel: string;
  /** 고른 것 중 빠지는 것 — 사유별. */
  excluded?: BulkExcludeGroup[];
  /** 실행 뒤 되돌릴 수 있는지 한두 문장. */
  revertNote: React.ReactNode;
  /** 무엇을 하는지 한 줄(쓰는 쪽 문장). */
  description?: React.ReactNode;
  okText: string;
  onOk: () => void;
  onCancel: () => void;
}

/**
 * 일괄 실행 전 미리보기(결정 D6 — 금액 변경이라 건수와 상관없이 늘 같은 흐름) — 감사 card-controltower#696, 설계 §2-2.
 *   카드 | 지금 값 → 바뀔 값 | 차이   (최대 5줄, 변동이 큰 순) + 외 N건
 *   제외 N건 — 사유별 건수
 *   되돌리기 — 실행 뒤 되돌릴 수 있는지
 * AppModal 뼈대 위에 얹은 합성 컴포넌트(DESIGN_SYSTEM §5-4). 보내기 자체는 onOk 를 받은 쪽이 한다.
 */
export const BulkPreviewModal: React.FC<BulkPreviewModalProps> = ({
  open, title, items, fromLabel, toLabel, excluded = [], revertNote, description, okText, onOk, onCancel,
}) => {
  const t = useThemeTokens();
  const phone = useIsPhone();
  const shown = previewRows(items);
  const rest = items.length - shown.length;
  const reasons = excluded.filter((g) => g.count > 0);
  const excludedCount = reasons.reduce((n, g) => n + g.count, 0);
  const cell: React.CSSProperties = {
    padding: `${SPACING.xxs}px ${SPACING.xs}px`, borderBottom: `1px solid ${t.borderSubtle}`,
    fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.normal,
  };
  const num: React.CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  // 전후 값은 표 규칙의 숫자 칸(NumberCell — 미수집 · 0 구분, 고정폭 숫자, 감사 card-controltower#714). 문장 속이라 단위를 칸에 붙인다.
  const price = (n: number | null | undefined, to = false) => (
    <NumberCell value={n} withUnit bold={to} color={to ? t.textPrimary : t.textSecondary} />
  );
  const diffColor = (it: BulkPreviewItem) => {
    const d = it.from != null && it.to != null ? it.to - it.from : 0;
    return d > 0 ? t.success : d < 0 ? t.danger : t.textSecondary;
  };

  return (
    <AppModal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      okText={okText}
      cancelText="취소"
      okButtonProps={{ disabled: items.length === 0 }}
      width={560}
    >
      <div data-testid="bulk-preview" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        {description && (
          <div style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, lineHeight: LINE_HEIGHT.relaxed }}>{description}</div>
        )}
        {items.length === 0 ? (
          <div style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>보낼 카드가 없습니다.</div>
        ) : (
          <div>
            {phone ? (
              // 390 은 세로 배치(코덱 리뷰 #717) — 카드 이름(2줄 말줄임) 아래에 전후 값 · 차이.
              <ul data-testid="bulk-preview-rows" aria-label={`${fromLabel} → ${toLabel}`} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {shown.map((it) => (
                  <li key={it.key} style={{ ...cell, display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
                    <span title={it.name} style={{ ...clamp2, color: t.textPrimary }}>{it.name}</span>
                    <span style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: SPACING.xs, fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{ color: t.textSecondary }}>
                        {price(it.from)} → {price(it.to, true)}
                      </span>
                      <span style={{ fontWeight: FONT_WEIGHT.semibold, color: diffColor(it) }}>{diffText(it.from, it.to)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              // 열 폭을 고정해 긴 카드 이름이 모달 폭을 넘지 않게 — 이름은 2줄 말줄임, 금액 · 차이 열은 최소 폭(코덱 리뷰 #717).
              <table data-testid="bulk-preview-rows" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: PRICE_COL_W }} />
                  <col style={{ width: DIFF_COL_W }} />
                </colgroup>
                <thead>
                  <tr style={{ color: t.textSecondary }}>
                    <th style={{ ...cell, textAlign: 'left', fontWeight: FONT_WEIGHT.medium }}>카드</th>
                    <th style={{ ...cell, textAlign: 'right', fontWeight: FONT_WEIGHT.medium }}>{fromLabel} → {toLabel}</th>
                    <th style={{ ...cell, textAlign: 'right', fontWeight: FONT_WEIGHT.medium }}>차이</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((it) => (
                    <tr key={it.key}>
                      <td style={{ ...cell, color: t.textPrimary }} title={it.name}>
                        <span style={clamp2}>{it.name}</span>
                      </td>
                      <td style={{ ...num, color: t.textSecondary }}>
                        {price(it.from)} → {price(it.to, true)}
                      </td>
                      <td style={{ ...num, fontWeight: FONT_WEIGHT.semibold, color: diffColor(it) }}>
                        {diffText(it.from, it.to)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rest > 0 && (
              <div data-testid="bulk-preview-rest" style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginTop: SPACING.xxs }}>
                외 {rest.toLocaleString()}건 — 모두 {items.length.toLocaleString()}건
              </div>
            )}
          </div>
        )}
        {excludedCount > 0 && (
          <div data-testid="bulk-preview-excluded" style={{
            fontSize: FONT_SIZE.xs, color: t.textPrimary, background: t.bgRaised, border: `1px solid ${t.border}`,
            borderRadius: RADIUS.md, padding: `${SPACING.xs}px ${SPACING.sm}px`, lineHeight: LINE_HEIGHT.relaxed,
          }}>
            {/* 업무 상태 '대상 아님' 알약(StatusBadge, 감사 #714) — 색 + 글자. */}
            <StatusBadge axis="work" status="excluded" label={`제외 ${excludedCount.toLocaleString()}건`} testId="bulk-preview-excluded-badge" />
            {' '}— 보내지 않습니다:{' '}
            {reasons.map((g) => `${g.label} ${g.count.toLocaleString()}`).join(' · ')}
          </div>
        )}
        <div data-testid="bulk-preview-revert" style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, lineHeight: LINE_HEIGHT.relaxed }}>
          <b style={{ color: t.textPrimary }}>되돌리기</b> — {revertNote}
        </div>
      </div>
    </AppModal>
  );
};
